import { createContext, useContext, useSyncExternalStore } from 'react';
import {
  DISCOVERY_PROTOCOL_VERSION,
  DISCOVERY_PROTOCOL_HEADER,
  REGISTRY_VERSION_HEADER,
  type TerritoryCapabilities,
  type DiscoveryDescriptor,
  type LocationChoice,
  type TerritoryResolution,
  type ResolvedMunicipality,
} from '@buzzmap/shared';

/**
 * Leaf module for the route-owned discovery state (T010-FE).
 *
 * It exists to break the module cycle the T010 review flagged (S1):
 * `DiscoveryContext.tsx` → `routes/usePrimaryRoutesLocation.ts` → hooks barrel
 * → `useUserPreferences.ts` → back into the context module. The context
 * OBJECT and its consumer hooks live here with zero non-type imports beyond
 * React, so `useUserPreferences` / `EventsContext` can read the discovery
 * state without importing the provider module. The provider itself
 * (`DiscoveryContext.tsx`) imports this file and re-exports these names, so
 * existing `from './DiscoveryContext'` imports keep working.
 */

/** Release gate — see the provider's doc comment for the full mode table. */
export type DiscoveryMode = 'loading' | 'legacy' | 'v2' | 'upgrade-required';
export type DiscoveryStatus = 'idle' | 'pending' | 'ready' | 'invalid' | 'unavailable';

/**
 * State of a legacy geographic hint (`?city=`/`province`, `centers`) carried
 * by a generic entry (plan l.61, B2 of the T010 review):
 * - `none`: no hint (or already resolved into a replacement).
 * - `resolving`: the registry/containment lookup is in flight.
 * - `needs-selection`: ambiguous (homonym without province, multiple covering
 *   polygons) — the feed is HELD and `hintCandidates` carries the choices;
 *   T013 renders the prompt. Never a silent first-match guess.
 * - `invalid`: unresolvable (no registry match / containment unavailable) —
 *   the feed is HELD; plan l.61 forbids falling back to broader results.
 */
export type DiscoveryHintStatus = 'none' | 'resolving' | 'needs-selection' | 'invalid';

/**
 * Why the client is in `upgrade-required` (T016-FE):
 * - `protocol`: the server demands a higher `minimumDiscoveryProtocol`, or a
 *   discovery request was rejected with HTTP 426 even after renegotiation.
 * - `maintenance`: the epoch was known to be active and the capabilities
 *   bootstrap now fails/times out while the browser is online — the reference
 *   is unreachable, so the client must show the explicit maintenance state and
 *   NEVER fall back to legacy behaviour (plan l.71, T010 W1).
 */
export type DiscoveryUpgradeReason = 'protocol' | 'maintenance';

export interface DiscoveryContextType {
  mode: DiscoveryMode;
  capabilities: TerritoryCapabilities | null;
  /** Normalized descriptor for the current URL (null on non-discovery routes). */
  descriptor: DiscoveryDescriptor | null;
  status: DiscoveryStatus;
  resolution: TerritoryResolution | null;
  settled: boolean;
  /** v2 authority: active LocationChoice from the resolved territory, or null. */
  activeLocation: LocationChoice | null;
  /** True while a v2 resolution/entry/hint decision is pending (gates feed fetches). */
  pending: boolean;
  /** v2: URL to replace a generic localized entry to, or null. */
  entryReplacementTarget: string | null;
  /** v2: state of the current legacy geographic hint (see DiscoveryHintStatus). */
  hintStatus: DiscoveryHintStatus;
  /** v2: candidates for an ambiguous hint (selection prompt input, T013). */
  hintCandidates: ResolvedMunicipality[];
  /** Why `upgrade-required` is in force, or null in any other mode (T016). */
  upgradeReason: DiscoveryUpgradeReason | null;
  /** Explicit Italia: cancel pending work, clear the visit fallback, stay national. */
  explicitItalia: () => void;
  /** Re-run the capabilities bootstrap and the current resolution. */
  retry: () => void;
  /**
   * A discovery request was rejected with HTTP 426 (T016-FE, plan l.74): mark
   * the epoch as known-active and trigger ONE bounded renegotiation. A second
   * 426 with fresh capabilities confirms the upgrade state (sticky until
   * `retry()`/reload) so the client can never loop on a hopeless bootstrap.
   * Safe to call from any discovery fetch site (EventsContext, the legacy
   * territory resolution, the provider itself).
   */
  reportUpgradeRequired: () => void;
}

export const DiscoveryContext = createContext<DiscoveryContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Mode signal (T011-FE)
// ---------------------------------------------------------------------------
//
// `UserProvider` MOUNTS ABOVE `DiscoveryProvider` (the provider reads the saved
// intent from it), so the preference owner cannot read `mode` from context.
// `DiscoveryProvider` publishes the computed gate here instead — one value,
// one capabilities fetch (the bootstrap stays in the provider), no second
// bootstrap. `useUserPrefsSync`/`UserProvider` subscribe so the v1 protocol is
// retired and the v2 store takes over the moment the gate opens; before that
// (default `'loading'`) everything behaves exactly as today.

let modeSnapshot: DiscoveryMode = 'loading';
const modeListeners = new Set<() => void>();

/** Publish the current gate. Called by `DiscoveryProvider`; no-op when unchanged. */
export function publishDiscoveryMode(mode: DiscoveryMode): void {
  if (mode === modeSnapshot) return;
  modeSnapshot = mode;
  for (const listener of [...modeListeners]) listener();
}

export function getDiscoveryMode(): DiscoveryMode {
  return modeSnapshot;
}

export function subscribeDiscoveryMode(listener: () => void): () => void {
  modeListeners.add(listener);
  return () => {
    modeListeners.delete(listener);
  };
}

/** Subscribe to the published gate (for consumers above `DiscoveryProvider`). */
export function useDiscoveryMode(): DiscoveryMode {
  return useSyncExternalStore(subscribeDiscoveryMode, getDiscoveryMode, getDiscoveryMode);
}

/** Test-only: restore the pre-bootstrap default between tests in one file. */
export function resetDiscoveryMode(): void {
  publishDiscoveryMode('loading');
}

/**
 * Full discovery context. Throws outside a `DiscoveryProvider` — use
 * `useDiscoveryOptional` on surfaces that may render without one (organizer
 * host, isolated unit tests).
 */
export function useDiscovery(): DiscoveryContextType {
  const context = useContext(DiscoveryContext);
  if (context === undefined) {
    throw new Error('useDiscovery must be used within a DiscoveryProvider');
  }
  return context;
}

/** Null when no `DiscoveryProvider` is mounted (organizer host, legacy tests). */
export function useDiscoveryOptional(): DiscoveryContextType | null {
  return useContext(DiscoveryContext) ?? null;
}

/**
 * Epoch metadata every v2 discovery request MUST declare (plan l.72, LIVE-CLIENT
 * EPOCH CONTRACT; B1 of the T010 review). `api/lib/discovery-epoch.ts` rejects
 * unversioned discovery GETs with 426 once `ready=true`, so a client that does
 * not send these cannot resolve anything at activation.
 *
 * Empty object while capabilities are unknown (bootstrap still in flight, or a
 * fault): the server's pre-activation gate is a pass-through, and post-activation
 * an unversioned request failing with 426 is the honest outcome (T016 renders the
 * negotiation/upgrade state).
 */
export function buildDiscoveryRequestHeaders(
  capabilities: TerritoryCapabilities | null
): Record<string, string> {
  if (!capabilities) return {};
  return {
    [DISCOVERY_PROTOCOL_HEADER]: String(DISCOVERY_PROTOCOL_VERSION),
    [REGISTRY_VERSION_HEADER]: capabilities.activeRegistryVersion,
  };
}
